import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import { type CoWorkerScheduleActivity } from "@/features/calendar/types";
import { updateSchedule } from "@repo/api";
import { Button } from "@repo/skapa";
import { t } from "@repo/translation";
import { ModalSheet, useSettings } from "@repo/ui";
import { formatDateToApiUTC } from "@repo/utils";
import { useState } from "react";

import { useModals } from "../hooks";
import { ScheduleActivityEdit } from "./ScheduleActivityEdit";
import { ScheduleActivityView } from "./ScheduleActivityView";

interface ScheduleActivityModalProps {
  isOpen: boolean;
  item: CoWorkerScheduleActivity;
  closeModal: () => void;
}

type Loading = "EDITING" | "DELETING";

export const ScheduleActivityModal = ({
  isOpen,
  closeModal,
  item,
}: ScheduleActivityModalProps) => {
  const { calendar } = useSettings();

  const { pushModal } = useModals();
  const { deleteSchedule } = useCalendarContext();

  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] =
    useState<CoWorkerScheduleActivity>(item);
  const [loading, setLoading] = useState<Loading | undefined>(undefined);

  const openEditMode = () => setEditMode(true);

  const handleSave = () => {
    setLoading("EDITING");
    updateSchedule(item.id, {
      shiftStartTime: formatDateToApiUTC(currentItem.start),
      shiftEndTime: formatDateToApiUTC(currentItem.end),
      activity: currentItem.activity,
      shiftType: currentItem.shiftType,
    }).then((res) => {
      setLoading(undefined);
      setCurrentItem({
        ...item,
        start: new Date(res.shiftStartTime),
        end: new Date(res.shiftEndTime),
        title: res.activity ?? res.shiftType,
        activity: res.activity,
        shiftType: res.shiftType,
      });
      setEditMode(false);
    });
  };

  const onDelete = () => {
    setLoading("DELETING");
    deleteSchedule(item.id).then(() => {
      setLoading(undefined);
      closeModal();
    });
  };

  return (
    <ModalSheet
      title={editMode ? t("action.edit_activity") : t("label.activity")}
      visible={isOpen}
      onClose={closeModal}
      onBack={
        editMode
          ? () => {
              setEditMode(false);
              setCurrentItem(item);
            }
          : undefined
      }
      footer={
        calendar?.edit_schedule_entries ? (
          editMode ? (
            <Button
              onClick={handleSave}
              type="primary"
              loading={loading === "EDITING"}
            >
              {t("action.save")}
            </Button>
          ) : (
            <>
              <Button
                onClick={openEditMode}
                type="primary"
                loading={loading === "EDITING"}
              >
                {t("action.edit")}
              </Button>
              {calendar?.delete_schedule_entries && (
                <Button
                  onClick={() =>
                    pushModal({
                      type: "CONFIRM",
                      onConfirm: onDelete,
                      title: t("action.delete_schedule"),
                      message: t("prompt.confirm_delete_schedule"),
                    })
                  }
                  type="secondary"
                  loading={loading === "DELETING"}
                >
                  {t("action.delete")}
                </Button>
              )}
            </>
          )
        ) : undefined
      }
    >
      {editMode ? (
        <ScheduleActivityEdit item={currentItem} setItem={setCurrentItem} />
      ) : (
        <ScheduleActivityView
          item={currentItem}
          onEdit={openEditMode}
          onDelete={onDelete}
          loading={loading}
        />
      )}
    </ModalSheet>
  );
};
